<template>
  <div class="add-invoice-body fade-in">
    <!-- GLOBAL LOADER -->
    <transition name="fade-slide">
      <div v-if="loading" class="global-loader">
        <div class="loader-spinner"></div>
        <p>Processing your request...</p>
      </div>
    </transition>

    <!-- HEADER -->
    <div class="section-header">
      <div class="title-area">
        <h2>Create New Estimate</h2>
        <p>Prepare and send professional estimates to your clients.</p>
      </div>
      <!-- <button class="btn-help">
        <i class="bx bx-help-circle"></i> Help
      </button> -->
    </div>

    <div class="invoice-layout">
      <!-- LEFT MAIN CONTENT -->
      <div class="main-pane">
        <!-- CLIENT INFO -->
        <div class="card-section animate-in">
          <h3><i class="bx bx-user"></i> Client Information</h3>
          <div class="form-grid">
            <div class="form-group full">
              <label>Select Client</label>
              <div class="client-select">
                <select v-model="form.client">
                  <option value="">Choose Client</option>
                  <option
                    v-for="client in clients"
                    :key="client.user_id"
                    :value="client.user_id"
                  >
                    {{ client.fname }} {{ client.lname }}
                  </option>
                </select>
                <button
                  class="btn-outline"
                  style="margin-top: 1rem;"
                  @click="showAddClientModal = true"
                >
                  <i class="bx bx-plus"></i> Add New Client
                </button>
              </div>
            </div>
          </div>

          <transition name="fade-slide">
            <div v-if="selectedClient" class="client-preview" style="margin-top: 1rem;">
              <p><b>Email:</b> {{ selectedClient.email }}</p>
              <p><b>Phone:</b> {{ selectedClient.mobile }}</p>
              <p><b>Address:</b> {{ selectedClient.address_line1 }}</p>
            </div>
          </transition>
        </div>

        <!-- ESTIMATE DETAILS -->
        <div class="card-section animate-in">
          <h3><i class="bx bx-receipt"></i> Estimate Details</h3>
          <div class="form-grid two-col">
            <div class="form-group">
              <label>Estimate No.</label>
              <input v-model="form.estimateRef" placeholder="EST-0001" readonly />
            </div>
            <div class="form-group">
              <label>Date</label>
              <input type="date" v-model="form.date" />
            </div>
            <div class="form-group">
              <label>Expiry Date</label>
              <input type="date" v-model="form.expiryDate" />
            </div>
            <div class="form-group">
              <label>Status</label>
              <select v-model="form.status">
                <option>Draft</option>
                <option>Sent</option>
                <option>Accepted</option>
                <option>Declined</option>
              </select>
            </div>
          </div>
        </div>

        <!-- LINE ITEMS -->
        <div class="card-section animate-in">
          <div class="section-header-mini">
            <h3><i class="bx bx-list-plus"></i> Line Items</h3>
            <button class="btn-black-sm" @click="addItemNow">
              <i class="bx bx-plus"></i> Add Item
            </button>
          </div>

          <transition-group name="fade-slide" tag="div" style="margin-top: 1rem;">
            <div v-for="(item, i) in form.items" :key="i" class="item-card-dual">
              <div class="item-top-row">
                <div class="custom-select">
                  <select v-model="item.name" @change="handleItemSelect($event, i)">
                    <option value="">Select Product</option>
                    <option
                      v-for="p in products"
                      :key="p.item_id"
                      :value="p.item_name"
                    >
                      {{ p.item_name }}
                    </option>
                  </select>
                </div>
                <div class="item-fields">
                  <input v-model.number="item.qty" type="number" min="1" placeholder="Qty" />
                  <input v-model.number="item.rate" type="number" min="0" placeholder="Rate" />
                  <span class="amount">
                    $
                    {{ computeItemTotal(item).toFixed(2) }}
                  </span>
                </div>
              </div>

              <div class="item-bottom-row">
                <div class="tax-mini">
                  <label>Tax:</label>
                  <select v-model="item.taxType">
                    <option value="none">None</option>
                    <option value="percent">%</option>
                    <option value="fixed">Fixed</option>
                  </select>
                  <input v-model.number="item.taxValue" type="number" min="0" placeholder="0" />
                </div>

                <div class="discount-mini">
                  <label>Discount:</label>
                  <select v-model="item.discountType">
                    <option value="none">None</option>
                    <option value="percent">%</option>
                    <option value="fixed">Fixed</option>
                  </select>
                  <input
                    v-model.number="item.discountValue"
                    type="number"
                    min="0"
                    placeholder="0"
                  />
                </div>

                <button class="btn-remove" @click="removeItem(i)">
                  <i class="bx bx-trash"></i> Remove
                </button>
              </div>
            </div>
          </transition-group>

          <div class="add-product-row">
            <button class="btn-outline-sm" @click="showAddProductModal = true">
              <i class="bx bx-plus-circle"></i> Add New Product
            </button>
          </div>
        </div>

        <!-- SUMMARY SECTION -->
        <div class="card-section summary animate-in">
          <h3><i class="bx bx-calculator"></i> Summary</h3>

          <div class="summary-row mt-2">
            <span>Subtotal</span><strong>${{ subtotal.toFixed(2) }}</strong>
          </div>

          <div class="tax-row mt-2">
            <select v-model="form.taxType">
              <option value="none">No Tax</option>
              <option value="percent">Tax (%)</option>
              <option value="fixed">Fixed Tax</option>
            </select>
            <input v-model.number="form.taxValue" type="number" placeholder="0" />
            <span>${{ computedTax.toFixed(2) }}</span>
          </div>

          <div class="tax-row mt-2">
            <select v-model="form.discountType">
              <option value="none">No Discount</option>
              <option value="percent">Discount (%)</option>
              <option value="fixed">Fixed Discount</option>
            </select>
            <input v-model.number="form.discountValue" type="number" placeholder="0" />
            <span>- ${{ computedDiscount.toFixed(2) }}</span>
          </div>

          <div class="form-group mt-2">
            <label>Other Amount</label>
            <input v-model.number="form.otherAmount" type="number" placeholder="0" />
          </div>

          <div class="summary-row total mt-3">
            <span>Total</span>
            <strong>${{ finalTotal.toFixed(2) }}</strong>
          </div>
        </div>

        <!-- NOTES -->
        <div class="card-section animate-in">
          <h3><i class="bx bx-edit"></i> Notes</h3>
          <textarea
            v-model="form.notes"
            placeholder="Add additional details..."
            rows="3"
          ></textarea>
        </div>
      </div>

      <!-- RIGHT ACTIONS -->
      <div class="side-pane">
        <div class="card-section sticky animate-in">
          <button
            class="btn-outline full"
            style="background-color: #000; color: white;"
            @click="submitEstimate"
          >
            <i class="bx bx-paper-plane"></i> Send Estimate
          </button>
          <button class="btn-outline full mt-3" @click="openPreview">
            <i class="bx bx-show"></i> Preview
          </button>
          <button
            class="btn-outline full mt-3"
            @click="openSettingsDrawer"
          >
            <i class="bx bx-cog"></i> Settings
          </button>
        </div>
      </div>
    </div>

    <!-- SETTINGS DRAWER -->
    <transition name="slide-drawer">
      <div v-if="showSettings" class="settings-drawer">
        <div class="drawer-header">
          <h3><i class="bx bx-cog"></i> Estimate Settings</h3>
          <button @click="closeSettingsDrawer" class="close-btn">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="drawer-content">
          <section class="branding-settings">
            <h4><i class="bx bx-palette"></i> Customize Style</h4>
            <div class="branding-grid">
              <div class="upload-card" @click="triggerUpload">
                <input
                  type="file"
                  ref="fileInput"
                  @change="handleFile"
                  style="display: none"
                />
                <template v-if="!form.image">
                  <div class="upload-icon"><i class="bx bx-image-add"></i></div>
                  <p>Upload Logo</p>
                </template>
                <img v-else :src="apiBaseUrl+form.image" alt="Logo" />
              </div>

              <div class="color-card">
                <label>Theme Color</label>
                <div class="color-picker">
                  <div
                    class="color-preview"
                    :style="{ backgroundColor: form.color }"
                  ></div>
                  <input type="color" v-model="form.color" />
                  <span class="color-code">{{ form.color.toUpperCase() }}</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </transition>

    <!-- Add Client Modal -->
    <transition name="fade-slide">
      <div
        v-if="showAddClientModal"
        class="modal-overlay"
        @click.self="showAddClientModal = false"
      >
        <div class="modal-card">
          <h3><i class="bx bx-user-plus"></i> Add New Client</h3>
          <div class="form-group">
            <label>First Name</label>
            <input v-model="newClient.fname" placeholder="Enter first name" />
          </div>
          <div class="form-group mt-1">
            <label>Last Name</label>
            <input v-model="newClient.lname" placeholder="Enter last name" />
          </div>
          <div class="form-group mt-1">
            <label>Email</label>
            <input v-model="newClient.email" type="email" placeholder="Enter email" />
          </div>
          <div class="form-group mt-1">
            <label>Mobile</label>
            <input v-model="newClient.mobile" type="text" placeholder="Enter phone" />
          </div>
          <div class="form-group mt-1">
            <label>Address</label>
            <textarea v-model="newClient.address" rows="2"></textarea>
          </div>
          <div class="modal-actions mt-2">
            <button class="btn-primary" @click="addNewClient">Save Client</button>
            <button class="btn-outline" @click="showAddClientModal = false">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Add Product Modal -->
    <transition name="fade-slide">
      <div
        v-if="showAddProductModal"
        class="modal-overlay"
        @click.self="showAddProductModal = false"
      >
        <div class="modal-card">
          <h3><i class="bx bx-box"></i> Add New Product / Item</h3>
          <div class="form-group mt-1">
            <label>Item Name</label>
            <input v-model="newProduct.item_name" placeholder="Enter item name" />
          </div>
          <div class="form-group mt-1">
            <label>Description</label>
            <textarea v-model="newProduct.item_desc" rows="2"></textarea>
          </div>
          <div class="form-grid two-col mt-1">
            <div class="form-group">
              <label>Price</label>
              <input v-model.number="newProduct.item_price" type="number" />
            </div>
            <div class="form-group">
              <label>Unit</label>
              <input v-model="newProduct.item_unit" placeholder="pcs / hrs / box" />
            </div>
          </div>
          <div class="form-grid two-col mt-1">
            <div class="form-group">
              <label>Tax (%)</label>
              <input v-model.number="newProduct.item_tax" type="number" />
            </div>
            <div class="form-group">
              <label>Discount (%)</label>
              <input v-model.number="newProduct.item_discount" type="number" />
            </div>
          </div>
          <div class="form-group mt-1">
            <label>Category</label>
            <input v-model="newProduct.item_category" />
          </div>
          <div class="form-group mt-1">
            <label>Status</label>
            <select v-model="newProduct.item_status">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div class="modal-actions mt-2">
            <button class="btn-primary" @click="addNewProduct">Save Item</button>
            <button class="btn-outline" @click="showAddProductModal = false">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ============================
     FULLSCREEN PREVIEW POPUP 
=============================== -->
<transition name="fade">
  <div v-if="showPreview" class="preview-fullscreen" @click.self="closePreview">
    <div class="preview-content">
      <button class="preview-close-btn" @click="closePreview">✕</button>

      <UniversalPreviewPrint
        :record="previewRecord"
        :branding="previewBranding"
        mainType="estimate"
      />
    </div>
  </div>
</transition>

  </div>
</template>

<script setup>
import { ref, computed, onMounted,nextTick } from "vue";
import { useToast } from "vue-toastification";
import UniversalPreviewPrint from "@/comps/UniversalPreviewPrint.vue";

import {
  createInvoice,
  uploadFile,
  getTableRowCount,
  getInvoiceById
} from "@/apis/invoiceapis";
import { getItems, addItem } from "@/apis/itemapis";
import { getClients, addClient } from "@/apis/clientapis";
import { apiBaseUrl } from "@/apis/appconst";
const showPreview = ref(false);
const previewRecord = ref({});
const previewBranding = ref({});


const toast = useToast();
const loading = ref(false);

/* === FORM DATA === */
const form = ref({
  id:"",
  color: "#111827",
  image: "",
  client: "",
  estimateRef: "",
  date: "",
  expiryDate: "",
  status: "Sent",
  notes: "",
  items: [
    {
      name: "",
      qty: 1,
      rate: 0,
      taxType: "none",
      taxValue: 0,
      discountType: "none",
      discountValue: 0,
    },
  ],
  currency: "USD",
  taxType: "none",
  taxValue: 0,
  discountType: "none",
  discountValue: 0,
  otherAmount: 0,
});

/* === STATE === */
const clients = ref([]);
const products = ref([]);
const showAddProductModal = ref(false);
const showAddClientModal = ref(false);
const newProduct = ref({
  item_name: "",
  item_desc: "",
  item_price: 0,
  item_unit: "",
  item_tax: "",
  item_discount: "",
  item_category: "",
  item_status: "active",
});
const newClient = ref({
  fname: "",
  lname: "",
  email: "",
  mobile: "",
  address: "",
});

function openPreview() {
  previewRecord.value = {
    number: form.value.estimateRef || "",
    date: form.value.date || "",
    due_date: form.value.expiryDate || "",

    client_name:
      (selectedClient.value?.fname || "") +
      " " +
      (selectedClient.value?.lname || ""),
    client_email: selectedClient.value?.email || "",
    client_mobile: selectedClient.value?.mobile || "",
    client_address: selectedClient.value?.address_line1 || "",

    subtotal: subtotal.value,
    discount_total: computedDiscount.value,
    tax_total: computedTax.value,
    other_amount: form.value.otherAmount || 0,
    total: finalTotal.value,

    notes: form.value.notes || "",

    items: form.value.items.map((it) => {
      const base = (Number(it.qty) || 0) * (Number(it.rate) || 0);
      const discount = computeItemDiscount(it);
      const tax = computeItemTax(it);

      return {
        name: it.name || "",
        qty: it.qty || 1,
        rate: it.rate || 0,
        base_amount: base,
        discount_amount: discount,
        tax_amount: tax,
        final_total: computeItemTotal(it),
      };
    }),
  };

  previewBranding.value = {
    logo: apiBaseUrl+form.value.image || "",
    title: "Estimate",
    email: "",     // No business email fields in estimate form
    phone: "",
    address: "",
    primary: form.value.color || "#111827",
    secondary: "#6b7280",
  };

  showPreview.value = true;
}

function closePreview() {
  showPreview.value = false;
}

const computeItemTax = (item) => {
  if (item.taxType === "percent") return (item.qty * item.rate * item.taxValue) / 100;
  if (item.taxType === "fixed") return item.taxValue;
  return 0;
};

const computeItemDiscount = (item) => {
  if (item.discountType === "percent") return (item.qty * item.rate * item.discountValue) / 100;
  if (item.discountType === "fixed") return item.discountValue;
  return 0;
};

const computeItemBase = (item) => {
  return (Number(item.qty) || 0) * (Number(item.rate) || 0);
};

const computeItemTotal = (item) => {
  const base = computeItemBase(item);
  const tax = computeItemTax(item);
  const discount = computeItemDiscount(item);
  return Math.max(base + tax - discount, 0);
};


const subtotal = computed(() => {
  return form.value.items.reduce(
    (t, item) => t + computeItemTotal(item),
    0
  );
});

const computedTax = computed(() => {
  const val = Number(form.value.taxValue) || 0;
  if (form.value.taxType === "percent") return (subtotal.value * val) / 100;
  if (form.value.taxType === "fixed") return val;
  return 0;
});

const computedDiscount = computed(() => {
  const val = Number(form.value.discountValue) || 0;
  if (form.value.discountType === "percent") return (subtotal.value * val) / 100;
  if (form.value.discountType === "fixed") return val;
  return 0;
});


const finalTotal = computed(() => {
  const other = Number(form.value.otherAmount) || 0;
  return Math.max(
    subtotal.value + computedTax.value - computedDiscount.value + other,
    0
  );
});


/* === CLIENT SELECTION === */
const selectedClient = computed(() =>
  clients.value.find((c) => c.user_id === form.value.client)
);

/* === FILE UPLOAD === */
const fileInput = ref(null);
const triggerUpload = () => fileInput.value.click();

async function handleFile(event) {
  const file = event.target.files[0];
  if (!file) return;
  const fd = new FormData();
  fd.append("file1", file);
  const res = await uploadFile(fd);
  if (res.status) {
    form.value.image = res.file1;
    toast.success("Logo uploaded successfully");
  } else toast.error("Logo upload failed");
}

/* === ITEM MANAGEMENT === */
const addItemNow = () => {
  form.value.items.push({
    name: "",
    qty: 1,
    rate: 0,
    taxType: "none",
    taxValue: 0,
    discountType: "none",
    discountValue: 0,
  });
};

const removeItem = (i) => form.value.items.splice(i, 1);

const handleItemSelect = (event, index) => {
  const name = event.target.value;
  const selected = products.value.find(p => p.item_name === name);
  form.value.items[index].rate = selected
    ? Number(selected.item_unit_price)
    : 0;
};


/* === LOAD CLIENTS & ITEMS === */
async function loadClients() {
  loading.value = true;
     const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
    const res = await getClients(userData.user_id, "", 0, "");
  if (res.status) clients.value = res.records;
  else toast.error("Failed to load clients");
  loading.value = false;
}

async function loadProducts() {
  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
  const res = await getItems(0,userData.user_id+"", "");
  if (res.status) products.value = res.records;
  else toast.error("Failed to load items");
}

/* === ADD NEW PRODUCT === */
const closeProductModal = () => (showAddProductModal.value = false);

const addNewProduct = async () => {
  if (!newProduct.value.item_name.trim()) return toast.warning("Item name is required");

  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
  const payload = {
    item_name: newProduct.value.item_name,
    item_desc: newProduct.value.item_desc,
    item_price: newProduct.value.item_price || 0,
    item_unit: newProduct.value.item_unit,
    item_tax: newProduct.value.item_tax || 0,
    item_discount: newProduct.value.item_discount || 0,
    item_category: newProduct.value.item_category,
    item_status: newProduct.value.item_status,
    item_by: userData.user_id || "",
  };

  toast.success("Saving item...");
  const res = await addItem(payload);
  if (res?.status) {
    toast.success(res.success || "Item created successfully");
    await loadProducts();
    closeProductModal();
    newProduct.value = {
      item_name: "",
      item_desc: "",
      item_price: 0,
      item_unit: "",
      item_tax: "",
      item_discount: "",
      item_category: "",
      item_status: "active",
    };
  } else toast.error(res?.success || "Failed to save item");
};

/* === ADD NEW CLIENT === */
const closeClientModal = () => (showAddClientModal.value = false);

const addNewClient = async () => {
  if (!newClient.value.fname || !newClient.value.email)
    return toast.warning("Client name and email are required");

  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
  const payload = {
    fname: newClient.value.fname,
    lname: newClient.value.lname,
    email: newClient.value.email,
    mobile: newClient.value.mobile,
    address: newClient.value.address,
    user_cre_by: userData.user_id || "",
  };

  toast.success("Saving client...");
  const res = await addClient(payload);
  if (res?.status) {
    toast.success("Client added successfully");
    await loadClients();
    closeClientModal();
  } else toast.error("Failed to add client");
};

/* === SUBMIT ESTIMATE === */
async function submitEstimate() {
  if (!form.value.client) {
      toast.warning("Please select a client");
      return;
    }

    if (!form.value.date) {
      toast.warning("Please select the Estimate Date");
      return;
    }

    if (!form.value.expiryDate) {
      toast.warning("Please select the Expiry Date");
      return;
    }

    // 🔸 Date validation: expiry must be after or equal to estimate date
    const estDate = new Date(form.value.date);
    const expDate = new Date(form.value.expiryDate);
    if (expDate < estDate) {
      toast.warning("Expiry date cannot be earlier than the estimate date");
      return;
    }

    // 🔸 At least one item required
    if (!form.value.items.length) {
      toast.warning("Please add at least one item");
      return;
    }

    // 🔸 Validate each item
    const invalidItem = form.value.items.find(
      (it) =>
        !it.name ||
        it.qty <= 0 ||
        it.rate < 0 ||
        isNaN(it.qty) ||
        isNaN(it.rate)
    );

    if (invalidItem) {
      toast.warning("Please check item name, quantity, and rate");
      return;
    }

    // 🔸 Validate total amount > 0
    if (finalTotal.value <= 0) {
      toast.warning("Total amount must be greater than zero");
      return;
    }

    // === Prepare Payload ===
    loading.value = true;
    toast.success("Processing your estimate...");


  loading.value = true;

  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
const formattedItems = form.value.items.map((item) => ({
  name: item.name || "",
  qty: Number(item.qty) || 0,
  rate: Number(item.rate) || 0,
  description: item.description || "",
  taxType: item.taxType || "none",
  taxValue: Number(item.taxValue) || 0,
  discountType: item.discountType || "none",
  discountValue: Number(item.discountValue) || 0,

  base_amount: computeItemBase(item),
  tax_amount: computeItemTax(item),
  discount_amount: computeItemDiscount(item),
  total_amount: computeItemTotal(item),
}));


 const payload = {
    invoice_id: form.value.id || "",
    invoice_number: form.value.estimateRef,        // Estimate no (EST-0001)
    invoice_ref: form.value.estimateRef,           // Same as above
    invoice_client_id: form.value.client,
    invoice_client_name: "",                       // Backend fills from DB
    invoice_date: form.value.date,
    invoice_due_date: form.value.expiryDate,       // Expiry date for estimate
    invoice_status: form.value.status,             // Draft / Sent / Accepted / Declined
    invoice_notes: form.value.notes,
    invoice_currency: form.value.currency,
    invoice_theme_color: form.value.color,
    invoice_logo: form.value.image,

    // Calculation fields
    invoice_subtotal: subtotal.value,
    invoice_global_tax_type: form.value.taxType,
    invoice_global_tax_value: form.value.taxValue,
    invoice_global_discount_type: form.value.discountType,
    invoice_global_discount_value: form.value.discountValue,
    invoice_other_amount: form.value.otherAmount,
    invoice_tax_amount: computedTax.value,
    invoice_total_amount: finalTotal.value,
    invoice_final_total: finalTotal.value,

    invoice_created_by: userData.user_id || "",

    // Items
    items: formattedItems,
};




  const res = await createInvoice("estimate", payload);
  loading.value = false;

  if (res.status) {
    toast.success("Estimate created successfully");
    window.location.href = "/invoices?type=estimates";
  } else toast.error(res.success || "Failed to create estimate");
}

/* === SETTINGS DRAWER === */
const showSettings = ref(false);
const openSettingsDrawer = () => (showSettings.value = true);
const closeSettingsDrawer = () => (showSettings.value = false);

/* === AUTO GENERATE NEXT ESTIMATE REF === */
// async function getNextEstimateNo() {
//   const countRes = await getTableRowCount("estimate");
//   if (countRes.status) {
//     const next = countRes.total_rows + 1;
//     form.value.estimateRef = `EST-${String(next).padStart(4, "0")}`;
//   }
// }

/* === INITIAL LOAD === */
onMounted(async () => {
  try {
    loading.value = true;

    // 🔹 Load base data first (clients + products)
    await Promise.all([loadClients(), loadProducts()]);

    const urlParams = new URLSearchParams(window.location.search);
    const estimateId = urlParams.get("id");

    if (estimateId) {
  const res = await getInvoiceById("estimate", estimateId);

  if (res?.status && res.record) {
    const est = res.record;

    // BASIC FIELDS
    form.value.id = estimateId;
    form.value.estimateRef = est.estimate_number || "";
    form.value.client = est.estimate_client_id || "";
    form.value.date = est.estimate_date || "";
    form.value.expiryDate = est.estimate_expiry_date || "";
    form.value.status = est.estimate_status || "Draft";
    form.value.currency = est.estimate_currency || "USD";
    form.value.notes = est.estimate_notes || "";
    form.value.color = est.estimate_theme_color || "#111827";
    form.value.image = apiBaseUrl+est.estimate_logo || "";

    // 🔥 NEW GLOBAL TAX / DISCOUNT / OTHER / FINAL TOTAL (CORRECT)
    form.value.taxType = est.estimate_global_tax_type || "none";
    form.value.taxValue = parseFloat(est.estimate_global_tax_value || 0);

    form.value.discountType = est.estimate_global_discount_type || "none";
    form.value.discountValue = parseFloat(est.estimate_global_discount_value || 0);

    form.value.otherAmount = parseFloat(est.estimate_other_amount || 0);

    // Line Items
    if (Array.isArray(est.items) && est.items.length > 0) {
      form.value.items = est.items.map((it) => ({
        name: it.estimate_item_name || "",
        qty: parseFloat(it.estimate_item_qty || 1),
        rate: parseFloat(it.estimate_item_rate || 0),
        description: it.estimate_item_description || "",
        taxType: it.estimate_item_tax > 0 ? "percent" : "none",
        taxValue: parseFloat(it.estimate_item_tax || 0),
        discountType: it.estimate_item_discount > 0 ? "percent" : "none",
        discountValue: parseFloat(it.estimate_item_discount || 0),
      }));
    } else {
      form.value.items = [
        {
          name: "",
          qty: 1,
          rate: 0,
          description: "",
          taxType: "none",
          taxValue: 0,
          discountType: "none",
          discountValue: 0,
        },
      ];
    }

    await nextTick();
  }
}
 else {
      // 🔹 If no ID (new estimate), auto-generate next reference
      const countRes = await getTableRowCount("estimate");
      if (countRes.status) {
        const next = countRes.total_rows + 1;
        form.value.estimateRef = `EST-${String(next).padStart(4, "0")}`;
      }
    }
  } catch (err) {
    console.error("onMounted (estimate) error:", err);
  } finally {
    loading.value = false;
  }
});

</script>

<style scoped>
/* === GLOBAL LOADER === */
.global-loader {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(3px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 3000;
}

.loader-spinner {
  width: 48px;
  height: 48px;
  border: 5px solid #d1d5db;
  border-top: 5px solid #111827;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 0.8rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.global-loader p {
  color: #111827;
  font-weight: 500;
  font-size: 0.95rem;
}

/* === MAIN CONTAINER === */
.add-invoice-body {
  background: #fff;
  border-radius: 1.5rem;
  padding: 2rem 2.5rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
  font-family: "Inter", sans-serif;
}

/* === HEADER === */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.5rem;
}
.section-header h2 {
  font-size: 1.9rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.3rem;
}
.section-header p {
  color: #6b7280;
}
.btn-help {
  background: #111827;
  color: #fff;
  border-radius: 8px;
  border: none;
  padding: 0.7rem 1.3rem;
  cursor: pointer;
  transition: all 0.25s ease;
}
.btn-help:hover {
  background: #1f2937;
  transform: translateY(-1px);
}

/* === LAYOUT === */
.invoice-layout {
  display: flex;
  gap: 2.2rem;
}
.main-pane {
  width: 100%;
}

/* === CARD SECTION === */
.card-section {
  background: #f9fafb;
  border-radius: 1rem;
  padding: 1.8rem;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
}
.card-section:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
}
.card-section h3 {
  margin-bottom: 1rem;
  font-size: 1.1rem;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.section-header-mini {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* === FORM GRID === */
.form-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}
.form-grid.two-col .form-group {
  flex: 1 1 calc(50% - 1rem);
}
.form-group.full {
  flex: 1 1 100%;
}
label {
  font-weight: 600;
  font-size: 0.9rem;
  color: #374151;
  margin-bottom: 0.3rem;
  display: block;
}
input,
select,
textarea {
  width: 100%;
  padding: 0.8rem;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 0.9rem;
  transition: border 0.3s ease;
}
input:focus,
select:focus,
textarea:focus {
  border-color: #111827;
  outline: none;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
}
textarea {
  resize: none;
}

/* === LINE ITEMS === */
.item-card-dual {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 1rem 1.2rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 6px rgba(17, 24, 39, 0.05);
  transition: all 0.3s ease;
}
.item-card-dual:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 14px rgba(17, 24, 39, 0.08);
}

/* === Top Row === */
.item-top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}
.item-fields {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}
.item-fields input {
  width: 80px;
  text-align: right;
}
.amount {
  font-weight: 700;
  color: #111827;
}

/* === Bottom Row (Tax & Discount) === */
.item-bottom-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.8rem;
  flex-wrap: wrap;
  gap: 0.8rem;
  border-top: 1px dashed #e5e7eb;
  padding-top: 0.6rem;
}
.item-bottom-row label {
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 600;
}
.tax-mini,
.discount-mini {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.tax-mini select,
.discount-mini select {
  width: 70px;
  padding: 0.4rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.85rem;
  background: #f9fafb;
  color: #111827;
}
.tax-mini input,
.discount-mini input {
  width: 80px;
  text-align: right;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.4rem;
  font-size: 0.85rem;
  background: #fff;
  transition: all 0.25s ease;
}
.tax-mini input:focus,
.discount-mini input:focus {
  border-color: #111827;
  box-shadow: 0 0 0 2px rgba(17, 24, 39, 0.1);
}

/* === REMOVE BUTTON === */
.btn-remove {
  background: transparent;
  border: none;
  color: #ef4444;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.3s ease;
}
.btn-remove:hover {
  color: #dc2626;
  transform: translateY(-1px);
}

/* === ADD PRODUCT ROW === */
.add-product-row {
  margin-top: 1rem;
  text-align: right;
  display: flex;
  justify-content: flex-end;
}
.btn-outline-sm {
  background: #fff;
  color: #111827;
  border: 1px solid #111827;
  border-radius: 8px;
  padding: 0.55rem 1.2rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.25s ease;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.08);
}
.btn-outline-sm:hover {
  background: #111827;
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(17, 24, 39, 0.15);
}
.btn-outline-sm i {
  font-size: 1rem;
  color: #111827;
  transition: transform 0.3s ease;
}
.btn-outline-sm:hover i {
  color: #fff;
  transform: rotate(90deg);
}

/* === SUMMARY SECTION === */
.summary {
  background: #111827;
  color: #fff;
  border-radius: 16px;
  padding: 1.8rem;
}
.summary h3 {
  color: #fff;
  font-size: 1.15rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.6rem;
  font-size: 0.9rem;
}
.summary-row.total {
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  margin-top: 0.8rem;
  padding-top: 0.8rem;
  font-weight: 700;
  font-size: 1rem;
}
.tax-row {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  align-items: center;
  gap: 0.8rem;
}
.tax-row select,
.tax-row input {
  border: 1px solid #374151;
  border-radius: 8px;
  padding: 0.7rem 1rem;
  background: #1f2937;
  color: #f9fafb;
  font-size: 0.9rem;
}
.tax-row span {
  background: #374151;
  color: #d1d5db;
  font-weight: 600;
  padding: 0.55rem 0.9rem;
  border-radius: 8px;
  font-size: 0.9rem;
  text-align: center;
  min-width: 45px;
}

/* === NOTES === */
textarea {
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 0.9rem;
  padding: 0.8rem;
  resize: none;
  transition: all 0.3s ease;
}
textarea:focus {
  border-color: #111827;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
}

/* === BRANDING SETTINGS === */
.branding-settings {
  margin-top: 1.2rem;
}
.branding-grid {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 1rem;
  align-items: center;
}
.upload-card {
  width: 110px;
  height: 110px;
  border-radius: 12px;
  background: #f9fafb;
  border: 2px dashed #d1d5db;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}
.upload-card:hover {
  border-color: #111827;
  background: #f3f4f6;
  transform: translateY(-2px);
}
.upload-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
}
.color-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.8rem 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.color-picker {
  display: flex;
  align-items: center;
  gap: 10px;
}
.color-preview {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
}
.color-picker input[type="color"] {
  border: none;
  width: 40px;
  height: 30px;
  cursor: pointer;
  background: transparent;
}
.color-code {
  font-size: 0.8rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 6px;
}

/* === MODALS === */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
.modal-card {
  background: #fff;
  padding: 2rem;
  border-radius: 16px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  animation: fadeIn 0.3s ease;
}
.modal-card h3 {
  font-size: 1.2rem;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.8rem;
}

/* === ANIMATIONS === */
.fade-in {
  animation: fadeIn 0.6s ease;
}
.fade-slide-enter-active {
  transition: all 0.3s ease;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* === RESPONSIVE === */
@media (max-width: 768px) {
  .branding-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  .upload-card {
    margin: 0 auto;
  }
}
/* === BUTTONS === */
.btn-black,
.btn-primary,
.btn-outline,
.btn-black-sm {
  transition: all 0.3s ease;
  font-weight: 500;
  
}
.btn-black,
.btn-primary {
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 0.8rem 1.4rem;
  display: inline-flex;
  align-items: center;
  text-align: center;
}
.btn-black:hover,
.btn-primary:hover {
  background: #1f2937;
  transform: translateY(-1px);
}
.btn-black-sm {
  background: #111827;
  color: #fff;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
}
.btn-outline {
  background: #fff;
  border: 1px solid #111827;
  color: #111827;
  border-radius: 10px;
  padding: 0.7rem 1.4rem;
}
.btn-outline:hover {
  background: #111827;
  color: #fff;
  transform: translateY(-1px);
}

/* === UTILITY CLASSES === */
.full {
  width: 100%;
}
.mt-1 {
  margin-top: 0.5rem;
}
.mt-2 {
  margin-top: 1rem;
}
.mt-3 {
  margin-top: 1.5rem;
}
.mt-4 {
  margin-top: 2rem;
}
.text-right {
  text-align: right;
}
.text-center {
  text-align: center;
}
.text-muted {
  color: #9ca3af;
}
.text-bold {
  font-weight: 600;
}
.text-primary {
  color: #111827;
}

/* === DIVIDER === */
.divider {
  position: relative;
  text-align: center;
  color: #9ca3af;
  margin: 1.2rem 0;
}
.divider::before,
.divider::after {
  content: "";
  position: absolute;
  top: 50%;
  width: 40%;
  height: 1px;
  background: #e5e7eb;
}
.divider::before {
  left: 0;
}
.divider::after {
  right: 0;
}

/* === STICKY SECTIONS (if sidebar used later) === */

.sticky {
  position: sticky;
  top: 110px;   /* below page header */
  z-index: 10;
}

/* === TABLE PLACEHOLDER (if used later for estimate items summary) === */
.estimate-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}
.estimate-table th,
.estimate-table td {
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.9rem;
}
.estimate-table th {
  text-align: left;
  background: #f3f4f6;
  color: #111827;
  font-weight: 600;
}
.estimate-table td {
  color: #374151;
}
.estimate-table tr:hover td {
  background: #f9fafb;
}

/* === MODAL FIXES === */
.modal-card input,
.modal-card select {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.7rem;
  font-size: 0.9rem;
  transition: all 0.25s ease;
}
.modal-card input:focus,
.modal-card select:focus {
  border-color: #111827;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
  outline: none;
}
.modal-actions button {
  border-radius: 8px;
}

/* === RESPONSIVE === */
@media (max-width: 1024px) {
  .invoice-layout {
    flex-direction: column;
  }
  .main-pane {
    width: 100%;
  }
}
@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.6rem;
  }
  .branding-grid {
    grid-template-columns: 1fr;
  }
  .item-fields input {
    width: 60px;
  }
  .summary {
    padding: 1.5rem;
  }
  .summary-row {
    font-size: 0.85rem;
  }
}
@media (max-width: 480px) {
  .item-card-dual {
    padding: 0.8rem;
  }
  .item-fields {
    flex-wrap: wrap;
    gap: 0.4rem;
  }
  .tax-mini input,
  .discount-mini input {
    width: 60px;
  }
}

/* === FADE + SLIDE ANIMATIONS === */
.fade-in {
  animation: fadeIn 0.6s ease;
}
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* === SCROLLBAR BEAUTIFY === */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-thumb {
  background: #9ca3af;
  border-radius: 8px;
}
::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
::-webkit-scrollbar-track {
  background: #f3f4f6;
}

/* === TRANSITIONS / HOVER === */
.card-section,
.btn-outline-sm,
.item-card-dual,
.upload-card {
  transition: all 0.3s ease;
}
.card-section:hover,
.upload-card:hover,
.btn-outline-sm:hover {
  transform: translateY(-2px);
}
/* === SUMMARY SECTION (Compact, lighter theme) === */
.summary {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 1.5rem;
  box-shadow: 0 4px 10px rgba(17, 24, 39, 0.05);
}
.summary h3 {
  color: #111827;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 1rem;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.6rem;
  font-size: 0.9rem;
  color: #374151;
}
.summary-row.total {
  border-top: 1px solid #e5e7eb;
  padding-top: 0.8rem;
  margin-top: 1rem;
  font-weight: 700;
  color: #111827;
  font-size: 1rem;
}

/* === TAX / DISCOUNT ROW === */
.tax-row {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 0.7rem;
  align-items: center;
}
.tax-row select,
.tax-row input {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.6rem 0.8rem;
  font-size: 0.9rem;
  color: #111827;
  background: #fff;
  transition: all 0.25s ease;
}
.tax-row select:focus,
.tax-row input:focus {
  border-color: #111827;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
  outline: none;
}
.tax-row span {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  color: #374151;
  font-weight: 600;
  border-radius: 8px;
  padding: 0.55rem 0.9rem;
  font-size: 0.85rem;
}

/* === DUAL LINE ITEM CARD === */
.item-card-dual {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 1rem 1.2rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 6px rgba(17, 24, 39, 0.05);
  transition: all 0.3s ease;
}
.item-card-dual:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 14px rgba(17, 24, 39, 0.08);
}

/* === Top Row (Main Fields) === */
.item-top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}
.item-fields {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}
.item-fields input {
  width: 80px;
  text-align: right;
}
.amount {
  font-weight: 700;
  color: #111827;
}

/* === Bottom Row (Tax + Discount + Remove) === */
.item-bottom-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.8rem;
  flex-wrap: wrap;
  gap: 0.8rem;
  border-top: 1px dashed #e5e7eb;
  padding-top: 0.6rem;
}
.item-bottom-row label {
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 600;
}

.tax-mini,
.discount-mini {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.tax-mini select,
.discount-mini select {
  width: 70px;
  padding: 0.4rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.85rem;
  background: #f9fafb;
  color: #111827;
}
.tax-mini input,
.discount-mini input {
  width: 80px;
  text-align: right;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.4rem;
  font-size: 0.85rem;
  background: #fff;
  transition: all 0.25s ease;
}
.tax-mini input:focus,
.discount-mini input:focus {
  border-color: #111827;
  box-shadow: 0 0 0 2px rgba(17, 24, 39, 0.1);
}

/* === REMOVE BUTTON === */
.btn-remove {
  background: transparent;
  border: none;
  color: #ef4444;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.3s ease;
}
.btn-remove:hover {
  color: #dc2626;
  transform: translateY(-1px);
}

/* === BRANDING (Logo + Color) === */
.branding-settings {
  margin-top: 1.2rem;
}
.branding-settings h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #111827;
  font-size: 1rem;
  margin-bottom: 1rem;
}
.branding-grid {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 1rem;
  align-items: center;
}

/* === Upload Card === */
.upload-card {
  width: 110px;
  height: 110px;
  border-radius: 12px;
  background: #f9fafb;
  border: 2px dashed #d1d5db;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
}
.upload-card:hover {
  border-color: #111827;
  background: #f3f4f6;
  transform: translateY(-2px);
}
.upload-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
  transition: all 0.3s ease;
}
.upload-card:hover img {
  transform: scale(1.05);
}
.upload-card .upload-icon i {
  font-size: 1.6rem;
  color: #6b7280;
}
.upload-card p {
  font-size: 0.8rem;
  color: #9ca3af;
  margin-top: 4px;
}

/* === Color Card === */
.color-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.8rem 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.color-card label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

/* === Color Picker === */
.color-picker {
  display: flex;
  align-items: center;
  gap: 10px;
}
.color-preview {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  box-shadow: inset 0 0 3px rgba(0, 0, 0, 0.05);
}
.color-picker input[type="color"] {
  border: none;
  width: 40px;
  height: 30px;
  cursor: pointer;
  background: transparent;
  padding: 0;
}
.color-code {
  font-size: 0.8rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 6px;
}

/* === Responsive Fixes === */
@media (max-width: 768px) {
  .branding-grid {
    grid-template-columns: 1fr;
  }
  .upload-card {
    margin: 0 auto;
  }
  .item-fields input {
    width: 60px;
  }
  .tax-mini input,
  .discount-mini input {
    width: 60px;
  }
  .summary {
    padding: 1.2rem;
  }
}

/* === Scrollbar (consistent look) === */
::-webkit-scrollbar {
  width: 8px;
}
::-webkit-scrollbar-thumb {
  background: #9ca3af;
  border-radius: 8px;
}
::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
::-webkit-scrollbar-track {
  background: #f3f4f6;
}

/* === Animations === */
.fade-in {
  animation: fadeIn 0.6s ease;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
/* === SETTINGS DRAWER (Right Side Panel) === */
.settings-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background: #ffffff;
  box-shadow: -6px 0 25px rgba(0, 0, 0, 0.12);
  border-radius: 1rem 0 0 1rem;
  display: flex;
  flex-direction: column;
  padding: 1.8rem 2rem;
  z-index: 1000;
  overflow-y: auto;
  transition: all 0.35s ease;
}

.settings-drawer::-webkit-scrollbar {
  width: 6px;
}
.settings-drawer::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 10px;
}
.settings-drawer::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* === Drawer Header === */
.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.8rem;
}
.drawer-header h3 {
  font-size: 1.15rem;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.close-btn {
  border: none;
  background: none;
  font-size: 1.6rem;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.25s ease;
}
.close-btn:hover {
  color: #111827;
  transform: rotate(90deg);
}

/* === Drawer Content === */
.drawer-content {
  display: flex;
  flex-direction: column;
  gap: 1.8rem;
}
.drawer-content section {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.2rem 1.4rem;
  transition: all 0.3s ease;
}
.drawer-content section:hover {
  background: #f3f4f6;
  transform: translateY(-2px);
}
.drawer-content h4 {
  color: #111827;
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 6px;
}
.drawer-content p {
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.4;
}

/* === Input Elements inside Drawer === */
.drawer-content input[type="color"],
.drawer-content input[type="file"],
.drawer-content select {
  width: 100%;
  padding: 0.7rem;
  margin-top: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  font-size: 0.9rem;
  color: #111827;
  transition: all 0.3s ease;
}
.drawer-content input[type="color"] {
  height: 40px;
  padding: 0;
  cursor: pointer;
}
.drawer-content input[type="file"]::-webkit-file-upload-button {
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  margin-right: 0.5rem;
}
.drawer-content select:focus,
.drawer-content input:focus {
  border-color: #111827;
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
  outline: none;
}

/* === Animation (Slide Drawer) === */
.slide-drawer-enter-active,
.slide-drawer-leave-active {
  transition: transform 0.4s ease, opacity 0.3s ease;
}
.slide-drawer-enter-from,
.slide-drawer-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* === Buttons inside Drawer === */
.drawer-content .btn-black,
.drawer-content .btn-primary {
  background: #111827;
  color: #fff;
  border-radius: 10px;
  border: none;
  padding: 0.8rem 1.4rem;
  width: 100%;
  text-align: center;
  font-weight: 500;
  transition: all 0.3s ease;
}
.drawer-content .btn-black:hover,
.drawer-content .btn-primary:hover {
  background: #1f2937;
  transform: translateY(-2px);
}

/* === Responsive Drawer === */
@media (max-width: 992px) {
  .settings-drawer {
    width: 100%;
    border-radius: 1rem 1rem 0 0;
    height: 90vh;
  }
}
/* === 🧾 PATCH: Line Items Layout Fix (Desktop + Mobile) === */

/* Desktop (≥992px) */
@media (min-width: 992px) {
  .item-top-row {
    display: grid !important;
    grid-template-columns: 1.6fr 1.2fr auto !important;
    align-items: center !important;
    gap: 1rem !important;
  }

  .custom-select select {
    width: 100% !important;
    min-width: 180px !important;
  }

  .item-fields {
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    gap: 0.8rem !important;
  }

  .item-fields input {
    width: 90px !important;
    text-align: right !important;
  }

  .item-fields .amount {
    min-width: 100px !important;
    text-align: right !important;
    font-weight: 700 !important;
    color: #111827 !important;
  }

  .item-bottom-row {
    display: grid !important;
    grid-template-columns: repeat(3, auto) 1fr !important;
    align-items: center !important;
    gap: 1.2rem !important;
  }

  .item-bottom-row .tax-mini,
  .item-bottom-row .discount-mini {
    display: flex !important;
    align-items: center !important;
    gap: 0.4rem !important;
  }

  .item-bottom-row .btn-remove {
    justify-self: end !important;
  }
}

/* Mobile (≤768px) */
@media (max-width: 768px) {
  .item-card-dual {
    padding: 1rem !important;
  }

  .item-top-row {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.8rem !important;
  }

  .item-fields {
    width: 100% !important;
    justify-content: space-between !important;
  }

  .item-fields input {
    width: 60px !important;
  }

  .item-bottom-row {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.8rem !important;
  }

  .btn-remove {
    align-self: flex-end !important;
  }
}

/* === ⚙️ PATCH: Scrollable Drawer on Mobile === */
@media (max-width: 992px) {
  .settings-drawer {
    height: 90vh !important;
    overflow-y: auto !important;
    padding-bottom: 2rem !important;
    -webkit-overflow-scrolling: touch !important;
  }

  .settings-drawer::-webkit-scrollbar {
    width: 6px;
  }

  .settings-drawer::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 10px;
  }

  .settings-drawer::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
}
/* === 📱 MOBILE RESPONSIVE PATCH === */
@media (max-width: 768px) {

  /* === General Page Layout === */
  .add-invoice-body {
    padding: 1.2rem !important;
    border-radius: 1rem !important;
  }

  .section-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.8rem !important;
  }

  .section-header h2 {
    font-size: 1.4rem !important;
  }

  .section-header p {
    font-size: 0.9rem !important;
  }

  .btn-help {
    align-self: flex-start !important;
    font-size: 0.85rem !important;
    padding: 0.5rem 1rem !important;
  }

  /* === Card Sections === */
  .card-section {
    padding: 1.2rem !important;
    border-radius: 0.8rem !important;
  }

  /* === Form Elements === */
  .form-grid.two-col {
    flex-direction: column !important;
  }

  .form-group input,
  .form-group select,
  .form-group textarea {
    font-size: 0.9rem !important;
    padding: 0.7rem 0.9rem !important;
  }

  /* === Line Items === */
  .item-card-dual {
    padding: 1rem !important;
    border-radius: 10px !important;
  }

  .item-top-row {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.6rem !important;
  }

  .item-fields {
    width: 100% !important;
    justify-content: space-between !important;
    gap: 0.6rem !important;
  }

  .item-fields input {
    width: 60px !important;
    padding: 0.5rem !important;
    text-align: right !important;
  }

  .item-bottom-row {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.8rem !important;
    padding-top: 0.6rem !important;
  }

  .item-bottom-row label {
    font-size: 0.8rem !important;
  }

  .tax-mini input,
  .discount-mini input {
    width: 60px !important;
  }

  .btn-remove {
    align-self: flex-end !important;
    font-size: 0.85rem !important;
    padding: 0.3rem 0.6rem !important;
  }

  /* === Add Product Button === */
  .add-product-row {
    justify-content: center !important;
  }

  .btn-outline-sm {
    width: 100% !important;
    text-align: center !important;
  }

  /* === Summary Section === */
  .summary {
    padding: 1.2rem !important;
  }

  .summary-row {
    font-size: 0.85rem !important;
  }

  /* === Notes === */
  textarea {
    font-size: 0.9rem !important;
    padding: 0.7rem !important;
  }

  /* === Settings Drawer (Right Sidebar) === */
  .settings-drawer {
    width: 100% !important;
    height: 90vh !important;
    border-radius: 1rem 1rem 0 0 !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch !important;
    padding: 1.5rem !important;
  }

  .settings-drawer::-webkit-scrollbar {
    width: 6px;
  }

  .settings-drawer::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 10px;
  }

  .settings-drawer::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }

  /* === Buttons === */
  .btn-primary,
  .btn-black,
  .btn-outline {
    width: 100% !important;
    text-align: center !important;
    font-size: 0.95rem !important;
  }

  .btn-black-sm {
    width: 100% !important;
  }

  /* === Drawer Sections === */
  .drawer-content section {
    padding: 1rem !important;
  }

  .branding-grid {
    grid-template-columns: 1fr !important;
    gap: 1rem !important;
  }

  .upload-card {
    margin: 0 auto !important;
  }
}

/* === 📱 Small Devices (≤480px) === */
@media (max-width: 480px) {
  .section-header h2 {
    font-size: 1.2rem !important;
  }

  .item-fields input {
    width: 50px !important;
  }

  .tax-mini input,
  .discount-mini input {
    width: 50px !important;
  }

  .summary {
    padding: 1rem !important;
    border-radius: 0.8rem !important;
  }

  .settings-drawer {
    height: 85vh !important;
  }

  .btn-help {
    width: 100% !important;
    justify-content: center !important;
  }

  .card-section {
    padding: 1rem !important;
  }
}
.preview-fullscreen {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4000;
  backdrop-filter: blur(4px);
  padding: 20px;
}

.preview-content {
  position: relative;
  width: 100%;
  max-width: 1000px;
  max-height: 95vh;
  background: #fff;
  border-radius: 12px;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
  animation: zoomIn 0.25s ease;
  padding: 20px;
}

.preview-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #111827;
  color: #fff;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 1.4rem;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: 0.25s;
}

.preview-close-btn:hover {
  background: #000;
  transform: scale(1.1);
}

@keyframes zoomIn {
  from { opacity: 0; transform: scale(0.92); }
  to { opacity: 1; transform: scale(1); }
}

</style>

